"use client";

import { useEffect, useRef } from "react";
import { cn } from "@/lib/utils";

interface StepIndicatorProps {
  currentStep: number;
  totalSteps: number;
}

export default function StepIndicator({
  currentStep,
  totalSteps,
}: StepIndicatorProps) {
  const progressRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (progressRef.current) {
      // Use CSS transition for smooth progress bar animation
      progressRef.current.style.width = `${(currentStep / totalSteps) * 100}%`;
    }
  }, [currentStep, totalSteps]);

  return (
    <div className="space-y-2 px-4 lg:px-0">
      <div className="flex justify-between px-2">
        {Array.from({ length: totalSteps }).map((_, index) => (
          <div key={index} className="flex flex-col items-center">
            <div
              className={cn(
                "flex h-8 w-8 items-center justify-center rounded-full border-2 text-xs font-medium transition-colors",
                currentStep > index + 1
                  ? "border-emerald-500 bg-emerald-500 text-white"
                  : currentStep === index + 1
                    ? "border-emerald-500 bg-white text-emerald-500"
                    : "border-slate-300 bg-white text-slate-500",
              )}
            >
              {index + 1}
            </div>
            <span className="mt-1 hidden text-xs text-slate-500 md:block">
              {index === 0
                ? "Identification"
                : index === 1
                  ? "Reproduction"
                  : index === 2
                    ? "Pregnancy"
                    : index === 3
                      ? "Birth"
                      : index === 4
                        ? "Development"
                        : index === 5
                          ? "Weaning"
                          : index === 6
                            ? "Physical Assessment"
                            : ""}
            </span>
          </div>
        ))}
      </div>

      <div className="relative h-1.5 w-full rounded-full bg-slate-200">
        <div
          ref={progressRef}
          className="absolute left-0 top-0 h-full rounded-full bg-emerald-500 transition-all duration-300 ease-in-out"
          style={{ width: `${(currentStep / totalSteps) * 100}%` }}
        />
      </div>
    </div>
  );
}
